
import '@/vee-validate-rules.ts';
import { Component, Vue, Prop, Model, Watch } from 'vue-property-decorator';
import { Getter, State } from 'vuex-class';
import { Rules } from '@/store/validations/types';
import ValidationAsterisk from '@/components/shared/ValidationAsterisk.vue';

// Declare an enum to use within the same component
enum BooleanRadioState {
  False,
  True,
}

@Component({
  components: {
    ValidationAsterisk
  }
})
export default class BooleanRadioInput extends Vue {
  @Getter('getRuleSet', { namespace: 'validations' }) private ruleSet!: Rules;
  @Getter('getRules', { namespace: 'validations' }) private getRules!: (ruleSet: any, ruleKey: string, rules: string) => any;
  @Getter('isReadOnly', { namespace: 'validations' }) private isReadOnly!: (readonly?: any) => boolean;
  @Getter('translateError', { namespace: 'utilities' }) private translateError!: (error?: any, field?: string|null) => string;

  // V-model
  @Model('change') value!: string;

  // Standard properties
  @Prop({ required: true }) inputId!: string; // MANDATORY actual HTML element ID, set indirectly using properties like 'inputId' and 'selectId'

  // Optional properties
  @Prop({ default: null }) validationId!: string; // OPTIONAL specify a 'vid' property for validation-provider, if it must be different than the element ID
                                                  // used by parent component after attempting to save to decide where server-side validation errors are shown
  @Prop({ default: null }) labelName!: string; // Optional label to prepend the radio buttons
  @Prop({ default: false }) hideLabel!: boolean; // Hide label visually, while still being readable for screen readers
  @Prop({ default: 'accept'}) acceptId!: string; // HTML ID for the accepted/true value
  @Prop({ default: 'decline'}) declineId!: string; // HTML ID for the declined/false value
  @Prop({ default: 'Accept'}) acceptLabel!: string; // Text label displayed for the accepted/true value
  @Prop({ default: 'Decline'}) declineLabel!: string; // Text label displayed for the declined/false value
  @Prop({ default: false }) disabled!: boolean; // Sets the input to disabled

  @Prop({ default: null }) rules!: string; // OPTIONAL lets us hard-code the client-side vee-validate rules in the front-end instead of using anything provided by the back-end
  @Prop({ default: null }) ruleKey!: string // OPTIONAL parameter path to load client-side validation e.g. new_validations, edit_validations
                                            // used by input components to set 'rules' properties in their validation providers based on the client-side validations loaded from the back-end
  @Prop({ default: null }) crossValues!: any; // valus needed for cross field validation for the asterix

  // Initialize local twin of model as null to ensure it exists during template render
  public localValue: BooleanRadioState|null = null;
  // Expose enumeration to the template
  public BOOLEAN_RADIO_STATE = BooleanRadioState;

  get formRules(): any {
    return this.getRules(this.ruleSet, this.ruleKey, this.rules);
  }

  // Set local twin of model when mounting to DOM
  public mounted(): void {
    if (this.value != null) {
      this.localValue = this.value ? this.BOOLEAN_RADIO_STATE.True : this.BOOLEAN_RADIO_STATE.False;
    } else {
      this.localValue = null;
    }
  }

  // Forward events to the parent component
  public inputEvents(): any {
    const _vm = this as BooleanRadioInput;
    return Object.assign({},
      // parent listeners
      this.$listeners,
      {
        // custom listeners
        change(event: any) {
          // Need to convert the enum value to a string to compare
          const isTrue = (event.target.value === _vm.BOOLEAN_RADIO_STATE.True.toString());
          // Emit updated value for v-model
          _vm.$emit('change', isTrue);
        }
      }
    );
  }

  // Watch for changes to the prop value and update localValue to match
  @Watch('value')
  private onValueChange(newValue: boolean, oldValue: boolean) {
    if (this.value != null) {
      this.localValue = this.value ? this.BOOLEAN_RADIO_STATE.True : this.BOOLEAN_RADIO_STATE.False;
    } else {
      this.localValue = null;
    }
  }
}
